frappe.provide("erpnext.accounts"); // @dokos

/**
 * PaymentRequestPanel – a slide-in side panel to ask a customer to pay online
 * directly from a Sales Invoice, Sales Order or Subscription, without ever
 * opening the technical Payment Request form. // @dokos
 *
 * Usage:
 *   new erpnext.accounts.PaymentRequestPanel({ frm, context }).show();
 *
 * `context` is the payload of
 *   erpnext.accounts.doctype.payment_request.payment_request.get_payment_request_context
 * and carries the *resolved* target document (for a Subscription this is the
 * current Sales Invoice / Sales Order), the amount due and sensible defaults.
 *
 * The single primary action creates AND submits the Payment Request and sends
 * the payment-link email in one step (backend: create_and_submit_payment_request).
 */
erpnext.accounts.PaymentRequestPanel = class PaymentRequestPanel {
	constructor({ frm, context }) {
		this.frm = frm;
		this.context = context || {};
		this.controls = {};
		this.wraps = {};
		this.schedules = [];
		this.amount = flt(this.context.amount_due);
		this.currency = this.context.currency || frm.doc.currency;

		this.overlay = new frappe.views.dodock_drawer({
			animation_type: "slide",
			backdrop: true,
			open_width: frappe.is_mobile() ? "100vw" : "420px",
			parent: $(this.frm.page.wrapper).find(".page-overlay"),
		});

		this._render_header();
		this._render_body();
		this._render_footer();

		if (this.context.has_schedules) {
			this._load_schedules();
		}
	}

	// ── DOM construction ──────────────────────────────────────────────────

	_render_header() {
		this.overlay.titleSection.textContent = "";
		this.overlay.titleSection.classList.add("prp-header-meta");

		const refEl = document.createElement("div");
		refEl.className = "prp-doc-ref";
		refEl.textContent = this.context.reference_name || this.frm.doc.name;

		const partyEl = document.createElement("div");
		partyEl.className = "prp-party-name";
		partyEl.textContent = this.context.party_name || "";

		this.overlay.titleSection.appendChild(refEl);
		this.overlay.titleSection.appendChild(partyEl);

		this._badgeEl = document.createElement("span");
		this._badgeEl.className = "prp-amount-badge";
		this._badgeEl.textContent = format_currency(this.amount, this.currency);
		this.overlay.rightSection.insertBefore(this._badgeEl, this.overlay.closingButton);

		this.overlay.topSection.style.background = "var(--subtle-fg)";
	}

	_render_body() {
		this.overlay.mainSection.className =
			"list-overlay-body flex-grow-1 overflow-auto p-4 d-flex flex-column";
		this.overlay.mainSection.style.gap = "1rem";

		const titleEl = document.createElement("div");
		titleEl.className = "prp-section-title";
		titleEl.textContent = __("Request Payment");
		this.overlay.mainSection.appendChild(titleEl);

		// Schedules (rendered lazily into this container when applicable)
		this._schedulesWrap = document.createElement("div");
		this._schedulesWrap.className = "prp-schedules";
		this._schedulesWrap.style.display = "none";
		this.overlay.mainSection.appendChild(this._schedulesWrap);

		this._add_control({
			fieldtype: "Data",
			fieldname: "email_to",
			label: __("Send To"),
			options: "Email",
			default: this.context.email_to || "",
			reqd: 1,
		});

		this._add_control({
			fieldtype: "Link",
			fieldname: "email_template",
			label: __("Email Template"),
			options: "Email Template",
			onchange: () => this._apply_template(),
		});

		this._add_control({
			fieldtype: "Data",
			fieldname: "subject",
			label: __("Subject"),
			default: __("Payment Request for {0}", [this.context.reference_name || this.frm.doc.name]),
			reqd: 1,
		});

		this._add_control({
			fieldtype: "Text Editor",
			fieldname: "message",
			label: __("Message"),
		});

		this._add_control({
			fieldtype: "Check",
			fieldname: "attach_pdf",
			label: __("Attach {0} PDF", [__(this.context.reference_doctype || this.frm.doctype)]),
		});

		if (!this.context.default_payment_gateway) {
			const note = document.createElement("div");
			note.className = "prp-note text-muted small";
			note.textContent = __(
				"No payment gateway is configured by default — the recipient will choose an available method on the payment page."
			);
			this.overlay.mainSection.appendChild(note);
		}
	}

	_render_footer() {
		this.overlay.bottomSection.className = "list-overlay-footer border-top p-3 d-flex flex-column";
		this.overlay.bottomSection.style.gap = "0.5rem";

		this._sendBtn = document.createElement("button");
		this._sendBtn.className = "btn btn-primary";
		this._sendBtn.textContent = __("Send Payment Link");
		this._sendBtn.addEventListener("click", () => this._submit(true));
		this.overlay.bottomSection.appendChild(this._sendBtn);

		const createBtn = document.createElement("button");
		createBtn.className = "btn btn-secondary";
		createBtn.textContent = __("Create without Sending");
		createBtn.addEventListener("click", () => this._submit(false));
		this.overlay.bottomSection.appendChild(createBtn);

		const fullFormBtn = document.createElement("button");
		fullFormBtn.className = "btn btn-link btn-sm text-muted";
		fullFormBtn.textContent = __("Open full Payment Request form");
		fullFormBtn.addEventListener("click", () => this._open_full_form());
		this.overlay.bottomSection.appendChild(fullFormBtn);
	}

	// ── Schedules ─────────────────────────────────────────────────────────

	async _load_schedules() {
		const { message } = await frappe.call({
			method: "erpnext.accounts.doctype.payment_request.payment_request.get_available_payment_schedules",
			args: {
				reference_doctype: this.context.reference_doctype,
				reference_name: this.context.reference_name,
			},
		});

		if (!message?.length) return;

		this.schedules = message.map((row) => ({ ...row, __selected: true }));
		this._schedulesWrap.style.display = "";
		this._schedulesWrap.innerHTML = "";

		const heading = document.createElement("div");
		heading.className = "prp-field-label";
		heading.textContent = __("Payment Schedules");
		this._schedulesWrap.appendChild(heading);

		this.schedules.forEach((row, idx) => {
			const line = document.createElement("label");
			line.className = "prp-schedule-row d-flex align-items-center justify-content-between";

			const left = document.createElement("span");
			const cb = document.createElement("input");
			cb.type = "checkbox";
			cb.checked = true;
			cb.className = "mr-2";
			cb.addEventListener("change", () => {
				this.schedules[idx].__selected = cb.checked;
				this._recompute_amount();
			});
			left.appendChild(cb);
			left.appendChild(
				document.createTextNode(`${row.payment_term || row.name} · ${row.due_date || ""}`)
			);

			const right = document.createElement("span");
			right.textContent = format_currency(row.payment_amount, this.currency);

			line.appendChild(left);
			line.appendChild(right);
			this._schedulesWrap.appendChild(line);
		});

		this._recompute_amount();
	}

	_recompute_amount() {
		if (this.schedules.length) {
			this.amount = this.schedules
				.filter((r) => r.__selected)
				.reduce((sum, r) => sum + flt(r.payment_amount), 0);
		}
		this._badgeEl.textContent = format_currency(this.amount, this.currency);
	}

	_selected_schedules() {
		return this.schedules.filter((r) => r.__selected);
	}

	// ── Email template ────────────────────────────────────────────────────

	async _apply_template() {
		const template = this.controls.email_template.get_value();
		if (!template) return;

		const r = await frappe.xcall("erpnext.accounts.doctype.payment_request.payment_request.get_message", {
			doc: JSON.stringify({
				reference_doctype: this.context.reference_doctype,
				reference_name: this.context.reference_name,
				grand_total: this.amount,
				currency: this.currency,
			}),
			template,
		});

		if (r?.subject) this.controls.subject.set_value(r.subject);
		if (r?.message) this.controls.message.set_value(r.message);
	}

	// ── Controls ──────────────────────────────────────────────────────────

	_add_control(df) {
		const wrap = document.createElement("div");
		wrap.className = "prp-field";
		this.overlay.mainSection.appendChild(wrap);

		const ctrl = frappe.ui.form.make_control({ df, parent: wrap, render_input: true });
		ctrl.refresh();
		if (df.default != null) {
			ctrl.set_value(df.default);
		}
		this.controls[df.fieldname] = ctrl;
		this.wraps[df.fieldname] = wrap;
		return ctrl;
	}

	_get_values() {
		const vals = {};
		Object.entries(this.controls).forEach(([k, c]) => (vals[k] = c.get_value()));
		return vals;
	}

	// ── Visibility ────────────────────────────────────────────────────────

	show() {
		this.overlay.open();
		this.controls.email_to?.$input?.focus();
	}

	hide() {
		this.overlay.close();
	}

	destroy() {
		this.overlay.close();
		$(this.overlay.wrapper).remove();
	}

	_open_full_form() {
		this.hide();
		frappe.new_doc("Payment Request", {
			reference_doctype: this.context.reference_doctype,
			reference_name: this.context.reference_name,
		});
	}

	// ── Submit ────────────────────────────────────────────────────────────

	async _submit(send_email) {
		const vals = this._get_values();
		const errors = [];

		if (send_email && !vals.email_to) errors.push(__("A recipient email is required to send the link"));
		if (this.schedules.length && !this._selected_schedules().length)
			errors.push(__("Select at least one payment schedule"));
		if (!flt(this.amount)) errors.push(__("Amount must be greater than 0"));

		if (errors.length) {
			frappe.msgprint({ title: __("Validation"), indicator: "red", message: errors.join("<br>") });
			return;
		}

		this._sendBtn.disabled = true;
		const original_label = this._sendBtn.textContent;
		this._sendBtn.textContent = send_email ? __("Sending…") : __("Creating…");

		try {
			const result = await frappe.xcall(
				"erpnext.accounts.doctype.payment_request.payment_request.create_and_submit_payment_request",
				{
					reference_doctype: this.context.reference_doctype,
					reference_name: this.context.reference_name,
					recipient_id: vals.email_to || null,
					mode_of_payment: vals.mode_of_payment || null,
					subject: vals.subject || null,
					message: vals.message || null,
					print_format: vals.attach_pdf ? this.context.default_print_format : null,
					schedules: this.schedules.length ? JSON.stringify(this._selected_schedules()) : null,
					send_email: send_email ? 1 : 0,
				}
			);

			if (result?.name) {
				frappe.show_alert(
					{
						message: send_email
							? __("Payment link sent · {0}", [
									`<a href="/app/payment-request/${result.name}">${result.name}</a>`,
							  ])
							: __("Payment Request {0} created", [
									`<a href="/app/payment-request/${result.name}">${result.name}</a>`,
							  ]),
						indicator: "green",
					},
					7
				);
				this.hide();
				this.frm.reload_doc();
			}
		} finally {
			this._sendBtn.disabled = false;
			this._sendBtn.textContent = original_label;
		}
	}
};
